/**
 * What streaming and social platforms pay per stream / use — one source of
 * truth for the /launch "Buy vs stream" slide and the /why-bb calculator
 * (Gordon 2026-10-29: "make sure the numbers are up to date with payment per
 * stream for all the streaming and social media platforms").
 *
 * Rates are USD, recording (master) side, to the rights holder BEFORE the
 * distributor's cut — so the streaming side of any comparison is generous.
 * Platforms don't pay a fixed rate per stream: these are total payout ÷ total
 * streams, from royalty statements, and vary by country, tier and deal.
 * Each figure keeps the most recent statement-based source we could verify;
 * `estimate` marks the ones that are industry estimates, not statement data.
 *
 * Re-check every few months and bump CHECKED_ON when you do.
 */

export const STREAM_RATES_CHECKED_ON = "2026-11-01";

/** Fallback only — the calculators convert with live rates from /api/fx.
 *  ECB reference rate 30 Oct 2026 (1 EUR = 1.1172 USD). */
export const USD_TO_EUR = 0.89509;

export type StreamUnit = "stream" | "view" | "use";

export interface StreamRate {
  key: string;
  label: string;
  /** USD per unit; null = no published rate. */
  usd: number | null;
  unit: StreamUnit;
  /** Kind of platform — streaming service vs social / video. */
  kind: "streaming" | "social";
  source: keyof typeof RATE_SOURCES;
  estimate?: boolean;
}

// Re-checked source by source on 2026-11-01 (each page opened, the figure
// confirmed, and a second, independent figure compared).
export const RATE_SOURCES = {
  ogrecords: {
    // One indie label's statements via its distributor, usage Apr–Jul 2026
    // (Germany-heavy): master side, before any distribution fee. We use its
    // WEIGHTED averages (medians: Spotify $0.0036, Apple $0.0074).
    label: "OGRECORDS (usage Apr-Jul 2026)",
    url: "https://www.og-records.com/en/streaming-report-2026",
  },
  duetti: {
    // Total payouts ÷ total streams, mostly US/UK, 2024 data (no newer
    // edition as of Nov 2026).
    label: "Duetti (2024 data)",
    url: "https://report.duetti.co",
  },
  soundcloud: {
    label: "SoundCloud",
    url: "https://soundcloud.com/topic/category/how-much-does-soundcloud-pay-per-stream/",
  },
  contentid: {
    // Dynamoi's own statement data: weighted average near $1,000 per million
    // claimed views, gross before distributor commission.
    label: "Dynamoi (Content ID)",
    url: "https://dynamoi.com/learn/statistics/content-id-revenue-benchmarks",
  },
  spotify: {
    // Since April 2024 a track needs 1,000 streams in 12 months to earn
    // recording royalties at all.
    label: "Spotify: monetization threshold",
    url: "https://support.spotify.com/us/artists/article/track-monetization-eligibility/",
  },
  soundexchange: {
    // US statutory per-play rates 2026: $0.0032 subscription, $0.0025 free.
    label: "SoundExchange (US, 2026)",
    url: "https://www.soundexchange.com/wp-content/uploads/2025/12/2026-Commercial-Webcaster-Memo.pdf",
  },
} as const;

export const STREAM_RATES: StreamRate[] = [
  { key: "amazon", label: "Amazon Music", usd: 0.0088, unit: "stream", kind: "streaming", source: "duetti" },
  { key: "apple", label: "Apple Music", usd: 0.0070, unit: "stream", kind: "streaming", source: "ogrecords" },
  { key: "tidal", label: "TIDAL", usd: 0.0068, unit: "stream", kind: "streaming", source: "duetti" },
  // Duetti's "YouTube" blends every YouTube revenue source, not YouTube Music alone.
  { key: "youtube_music", label: "YouTube (all programs)", usd: 0.0048, unit: "stream", kind: "streaming", source: "duetti" },
  // OGRECORDS pooled figure, marked indicative there (Duetti 2024: $0.0048).
  { key: "deezer", label: "Deezer", usd: 0.0038, unit: "stream", kind: "streaming", source: "ogrecords", estimate: true },
  { key: "spotify", label: "Spotify", usd: 0.0033, unit: "stream", kind: "streaming", source: "ogrecords" },
  // Matches the US statutory subscription rate per play.
  { key: "pandora", label: "Pandora (US)", usd: 0.0032, unit: "stream", kind: "streaming", source: "duetti" },
  // SoundCloud's own page: "approximately $2.50–$4.00 per 1,000 monetized streams" — midpoint.
  { key: "soundcloud", label: "SoundCloud", usd: 0.00325, unit: "stream", kind: "streaming", source: "soundcloud", estimate: true },
  { key: "youtube", label: "YouTube Content ID", usd: 0.0010, unit: "view", kind: "social", source: "contentid", estimate: true },
  // Per reported use in videos — mostly VIEWS, not video creations; TikTok
  // and Meta pay from licensing pools and publish no rate themselves.
  { key: "tiktok", label: "TikTok", usd: 0.0000072, unit: "use", kind: "social", source: "ogrecords" },
  { key: "instagram", label: "Instagram", usd: 0.000027, unit: "use", kind: "social", source: "ogrecords" },
  { key: "facebook", label: "Facebook", usd: 0.000027, unit: "use", kind: "social", source: "ogrecords" },
];

export const eurPerUnit = (r: StreamRate): number | null => (r.usd === null ? null : r.usd * USD_TO_EUR);
